import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { checkAuthThunk } from '@/stores/thunks/check-auth-thunk';
import { openFeedback } from '@/stores/feedback-slice';
import { Button } from '@/components/ui/button';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FeedbackDialog } from '@/components/dialogs/feedback-dialog';
import { FeedbackIcon } from '@/components/icons/feedback-icon';
import { PrimaryButton } from '@/components/ui/primary-button';

/**
 * Feedback button component that checks authentication before opening dialog
 */
export const FeedbackButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  
  const [showAuthAlert, setShowAuthAlert] = useState(false);

  // Check authentication on mount if not already checked
  useEffect(() => {
    if (isAuthenticated === null) {
      dispatch(checkAuthThunk());
    }
  }, [dispatch, isAuthenticated]);

  // Handle button click
  const handleClick = () => {
    if (isAuthenticated === false) {
      setShowAuthAlert(true);
    } else if (isAuthenticated === true) {
      dispatch(openFeedback());
    }
    // If null (still checking), do nothing
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        onClick={handleClick}
        disabled={isAuthenticated === null}
        title="Send feedback"
        aria-label="Send feedback"
        className="group"
      >
        <FeedbackIcon className="h-[1.2rem] w-[1.2rem] transition-transform group-hover:scale-125 group-active:scale-90" />
      </Button>

      {/* Auth Alert Dialog */}
      <AlertDialog open={showAuthAlert} onOpenChange={setShowAuthAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Feedback Unavailable</AlertDialogTitle>
            <AlertDialogDescription>
              Thanks for your interest in sending feedback about the <em>Sort Your Finances Out</em> app. To combat spam, we require users to be signed in to leave feedback. 
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogPrimitive.Action asChild>
              <PrimaryButton onClick={() => setShowAuthAlert(false)}>
                OK
              </PrimaryButton>
            </AlertDialogPrimitive.Action>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feedback Dialog */}
      <FeedbackDialog />
    </>
  );
};

export default FeedbackButton;
